import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getSessionId } from 'services/session/session.helpers';
import { selectSessionInfo } from 'services/session/session.selectors';
import { AppState } from 'store/types';
import { v4 as uuidV4 } from 'uuid';
import { prettifyError, ZodType } from 'zod';
import {
  apiRequestFailed,
  apiRequestSuccess,
  requestDataIfNecessary,
} from './api.actions';
import { CompoundLoadState, RequestLoadingState } from './api.types';

export function computeCompoundLoadState(
  ...loadStates: RequestLoadingState[]
): CompoundLoadState {
  const output = {
    hasLoadedAll: true,
    hasFailedToLoad: false,
    hasInvalidatedData: false,
  };

  for (let i = 0, len = loadStates.length; i < len; i++) {
    const { hasFailed, isInvalidated, isLoaded } = loadStates[i];

    if (output.hasLoadedAll) {
      output.hasLoadedAll = isLoaded;
    }

    if (!output.hasFailedToLoad) {
      output.hasFailedToLoad = hasFailed;
    }

    if (!output.hasInvalidatedData) {
      output.hasInvalidatedData = isInvalidated;
    }
  }

  return output;
}

/**
 * Returns a Predicate that returns `true` if the input action
 * matches `resourceId` and action type.
 * @param resourceId Unique Id of the fetched resource
 * @param apiActionCreator either
 * `apiRequestFailed` or `requestDataIfNecessary` or `apiRequestSuccess`
 */
export function createIsApiActionOf(
  apiActionCreator:
    | typeof apiRequestFailed
    | typeof requestDataIfNecessary
    | typeof apiRequestSuccess,
  resourceId: string,
): (action: any) => boolean {
  return function isApiActionOf(action: any) {
    if (!action || typeof action.type !== 'string') {
      return false;
    }

    return (
      apiActionCreator.match(action) && action.payload.resourceId === resourceId
    );
  };
}

export function getCommonHeaders(state: AppState) {
  const requestId = uuidV4();
  const sessionId = getSessionId();
  const { ipAddress, channel, channelInfo } = selectSessionInfo(state);

  return {
    'x-channel-id': channel?.toString(),
    'x-call-id': requestId,
    'x-session-id': sessionId,
    'x-channel-info': channelInfo,
    'x-ip-address': ipAddress,
  };
}

/**
 * Builds the SSO endpoint for the given endpoint. This is done because oauth and
 * sso flows are based on a different jwt token, and the services are exposed in
 * a separate endpoint. The SSO endpoint must follow the pattern: /{microservice}/{version}/sso/{rest},
 * if they don't, they have to be modified in the back-end accordingly.
 * Do not modify this logic.
 *
 * @param endpoint
 * @returns sso endpoint
 */
export function buildSsoEndpoint(endpoint: string) {
  if (endpoint.includes('/sso/')) return endpoint;

  const urlParts = endpoint.split('/');

  // exclude the original version and the word "ms" from the url
  const version = urlParts.find((urlPart) => /^v\d+$/.test(urlPart)) ?? 'v1';

  const strippedUrlParts = urlParts.filter((urlPart) => {
    return urlPart !== '' && urlPart !== 'ms' && !/^v\d+$/.test(urlPart);
  });

  const [microservice, ...rest] = strippedUrlParts;
  const ssoEndpoint = `/${microservice}/${version}/sso/${rest.join('/')}`;
  return ssoEndpoint;
}

export function isAxiosError<ResponseType>(
  error: unknown,
): error is AxiosError<ResponseType> {
  return !!(error && typeof error === 'object' && 'isAxiosError' in error);
}

export function logValidationErrors(
  successSchemaValidator: ZodType,
  method: AxiosRequestConfig['method'],
  url: string,
  response: AxiosResponse<unknown>,
) {
  const safeResultData = successSchemaValidator.safeParse(response.data);
  if (!safeResultData.success) {
    console.error(method, url, prettifyError(safeResultData.error));
  }
}
